/* Data layer entry: provider factory, React context, useData() hook, token persistence. */
import { createContext, createElement, useContext, useMemo, type ReactNode } from "react";
import { HttpProvider } from "./http/HttpProvider";
import { MockProvider } from "./mock/MockProvider";
import type { DataProvider } from "./provider";

export { ApiError } from "./provider";
export type { DataProvider } from "./provider";
export { HttpProvider } from "./http/HttpProvider";
export { MockProvider } from "./mock/MockProvider";

export type ProviderMode = "mock" | "http";
export const TOKEN_KEY = "starthr-token";

/* ---------- token persistence (localStorage may be unavailable) ---------- */
export function loadToken(): string | null {
  try { return globalThis.localStorage?.getItem(TOKEN_KEY) ?? null; } catch { return null; }
}
export function saveToken(token: string | null): void {
  try {
    if (token) globalThis.localStorage?.setItem(TOKEN_KEY, token);
    else globalThis.localStorage?.removeItem(TOKEN_KEY);
  } catch { /* private mode / blocked storage */ }
}

function envMode(): ProviderMode {
  let m: string | undefined;
  try { m = import.meta.env?.VITE_API_MODE as string | undefined; } catch { m = undefined; }
  return m === "http" ? "http" : "mock";
}

/** Wrap a provider so login/switchRole/logout/setToken persist the token in localStorage. */
export function withTokenPersistence<P extends DataProvider>(p: P): P {
  const login = p.login.bind(p), switchRole = p.switchRole.bind(p), logout = p.logout.bind(p), setToken = p.setToken.bind(p);
  const verifyEmail = p.verifyEmail.bind(p), changePassword = p.changePassword.bind(p);
  p.login = async (req) => { const r = await login(req); setToken(r.token); saveToken(r.token); return r; };
  p.switchRole = async (role) => { const r = await switchRole(role); setToken(r.token); saveToken(r.token); return r; };
  p.verifyEmail = async (token) => { const r = await verifyEmail(token); setToken(r.token); saveToken(r.token); return r; };
  p.changePassword = async (current, next) => { const r = await changePassword(current, next); setToken(r.token); saveToken(r.token); return r; };
  p.logout = async () => { try { await logout(); } finally { setToken(null); saveToken(null); } };
  p.setToken = (t) => { setToken(t); saveToken(t); };
  return p;
}

export interface CreateProviderOptions {
  /** Persist/restore the token in localStorage (default true). */
  persistToken?: boolean;
  /** Mock latency in ms (default 120 in the app). */
  latencyMs?: number;
  /** HTTP base URL (default VITE_API_URL ?? http://localhost:8080). */
  baseUrl?: string;
}

export function createProvider(mode: ProviderMode = envMode(), opts: CreateProviderOptions = {}): DataProvider {
  const persist = opts.persistToken ?? true;
  const token = persist ? loadToken() : null;
  const p: DataProvider = mode === "http"
    ? new HttpProvider({ baseUrl: opts.baseUrl, token })
    : new MockProvider({ latencyMs: opts.latencyMs ?? 120, token });
  return persist ? withTokenPersistence(p) : p;
}

/* ---------- React context ---------- */
export const DataProviderContext = createContext<DataProvider | null>(null);

export function DataProviderRoot({ provider, children }: { provider?: DataProvider; children?: ReactNode }) {
  const value = useMemo(() => provider ?? createProvider(), [provider]);
  return createElement(DataProviderContext.Provider, { value }, children);
}

export function useData(): DataProvider {
  const p = useContext(DataProviderContext);
  if (!p) throw new Error("useData() must be used inside <DataProviderRoot>");
  return p;
}
